import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import "../css/AdminDashboardLayout.css";

export default function AdminDashboardLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);

  useEffect(() => {
    setIsNavigationOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="admin-dashboard">
      <button
        className="admin-mobile-menu"
        type="button"
        aria-label="Open navigation"
        aria-expanded={isNavigationOpen}
        onClick={() => setIsNavigationOpen(true)}
      >
        <AdminIcon name="menu" />
        <span>Menu</span>
      </button>

      {isNavigationOpen && (
        <button
          className="admin-sidebar-backdrop"
          type="button"
          aria-label="Close navigation"
          onClick={() => setIsNavigationOpen(false)}
        />
      )}

      <aside className={`admin-sidebar${isNavigationOpen ? " is-open" : ""}`}>
        <div className="admin-sidebar-top">
          <div className="admin-brand">
            <div className="admin-logo-wrap">
              <Logo maxWidth="138px" marginBottom="0" />
            </div>
            <button
              className="admin-sidebar-close"
              type="button"
              aria-label="Close navigation"
              onClick={() => setIsNavigationOpen(false)}
            >
              <AdminIcon name="close" />
            </button>
          </div>

          <div className="admin-sidebar-heading">
            <span>Workspace</span>
            <strong>Admin Console</strong>
          </div>

          <nav className="admin-nav" aria-label="Admin dashboard navigation">
            <NavLink to="/dashboard" end>
              <AdminIcon name="home" />
              <span>Overview</span>
            </NavLink>
            <NavLink to="/dashboard/mailboxes">
              <AdminIcon name="mailbox" />
              <span>Mailboxes</span>
            </NavLink>
            <NavLink to="/dashboard/tenants">
              <AdminIcon name="users" />
              <span>Tenants</span>
            </NavLink>
            <NavLink to="/dashboard/tickets">
              <AdminIcon name="ticket" />
              <span>Tickets</span>
            </NavLink>
            <NavLink to="/dashboard/settings">
              <AdminIcon name="settings" />
              <span>Settings</span>
            </NavLink>
          </nav>
        </div>

        <div className="admin-sidebar-bottom">
          <button className="admin-logout" onClick={handleLogout} type="button">
            <AdminIcon name="logout" />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <section className="admin-content">
          <Outlet />
        </section>
      </main>
    </div>
  );
}

function AdminIcon({ name }) {
  const paths = {
    home: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5M9 21v-7h6v7" /></>,
    mailbox: <><path d="M4 20V9a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v11" /><path d="M2 20h20M12 4v10M12 8h5M16 8v3" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
    ticket: <><path d="M20 12a2 2 0 0 0 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 0 0-4V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 0-2 2Z" /><path d="M13 5v2M13 11v2M13 17v2" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21H9.6v-.1A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.5-1H3v-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.5V3h4v.1A1.7 1.7 0 0 0 15 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.5 1h.1v4h-.1a1.7 1.7 0 0 0-1.5 1Z" /></>,
    logout: <><path d="M10 17l5-5-5-5M15 12H3" /><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /></>,
    menu: <><path d="M4 6h16M4 12h16M4 18h16" /></>,
    close: <><path d="m6 6 12 12M18 6 6 18" /></>,
  };

  return (
    <svg className="admin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}
